import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Shield, 
  TrendingUp, 
  Smartphone, 
  Clock, 
  CheckCircle2, 
  Star,
  Home,
  Users,
  Umbrella,
  PiggyBank,
  ArrowRight
} from 'lucide-react';
import { mockTestimonials, features, howItWorksSteps, audienceSegments } from '../data/mockData';

const LandingPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleRegisterClick = () => {
    window.open('https://inv.investsimply.com.au/register', '_blank', 'noopener,noreferrer');
  };

  const audienceIcons = {
    Home: Home,
    Users: Users,
    Umbrella: Umbrella,
    PiggyBank: PiggyBank
  };

  return (
    <div className="landing-page">
      {/* Sticky Header */}
      <header className={`sticky-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            <img 
              src="https://customer-assets.emergentagent.com/job_f1049eb6-649d-420d-b932-127bcef1f941/artifacts/iyv7teig_SimpleInvest.svg" 
              alt="SimpleInvest Logo" 
              className="logo"
            />
            <Button 
              onClick={handleRegisterClick}
              className="cta-button"
              size="lg"
            >
              Start investing today
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content">
              <h1 className="hero-title">
                Invest with confidence
              </h1>
              <p className="hero-subtitle">
                Simple, transparent, expert-managed portfolios
              </p>
              <p className="hero-description">
                Low-cost, diversified investment portfolios designed for everyday Australians - Start in minutes.
              </p>
              
              <div className="hero-cta">
                <Button 
                  onClick={handleRegisterClick}
                  size="lg"
                  className="primary-cta"
                >
                  Start investing today
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>

              <div className="trust-badges">
                <Badge variant="outline" className="trust-badge">
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Managed by Franklin Templeton
                </Badge>
                <Badge variant="outline" className="trust-badge">
                  <Shield className="h-4 w-4 mr-2" />
                  Trusted by Unions and Industry Funds
                </Badge>
                <Badge variant="outline" className="trust-badge">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Driven by Smart Technology
                </Badge>
              </div>
            </div>

            <div className="hero-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_f1049eb6-649d-420d-b932-127bcef1f941/artifacts/46ls03qv_hero-happy-family.jpg"
                alt="Happy family investing together"
                className="rounded-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof & Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Trusted by everyday Australians</h2>
            <p className="section-subtitle">See what our investors have to say</p>
          </div>

          <div className="testimonials-grid">
            {mockTestimonials.map((testimonial, index) => (
              <Card key={index} className="testimonial-card">
                <CardContent className="testimonial-content">
                  <div className="stars">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="star-icon" fill="currentColor" />
                    ))}
                  </div>
                  <p className="testimonial-text">"{testimonial.text}"</p>
                  <div className="testimonial-author">
                    <div className="author-avatar">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="author-name">{testimonial.name}</p>
                      <p className="author-location">{testimonial.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features & Benefits */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">What you get</h2>
            <p className="section-subtitle">Everything you need to invest with confidence</p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => {
              const Icon = feature.icon === 'TrendingUp' ? TrendingUp :
                          feature.icon === 'Shield' ? Shield :
                          feature.icon === 'Smartphone' ? Smartphone : Clock;
              
              return (
                <Card key={index} className="feature-card">
                  <CardContent className="feature-content">
                    <div className="feature-icon">
                      <Icon className="h-8 w-8" />
                    </div>
                    <h3 className="feature-title">{feature.title}</h3>
                    <p className="feature-description">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">How it works</h2>
            <p className="section-subtitle">Get started in 4 simple steps</p>
          </div>

          <div className="steps-container">
            {howItWorksSteps.map((step, index) => (
              <div key={index} className="step-card">
                <div className="step-number">{step.number}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
                {index < howItWorksSteps.length - 1 && (
                  <ArrowRight className="step-arrow" />
                )}
              </div>
            ))}
          </div>

          {/* Platform Preview */}
          <div className="platform-preview">
            <div className="preview-grid">
              <div className="preview-item">
                <div className="preview-label">
                  <span className="preview-title">Start investing</span>
                </div>
                <div className="preview-image-container">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_simply-landing/artifacts/63zt0zap_Screenshot%202026-03-12%20at%2011.09.15%E2%80%AFam.png"
                    alt="Investment platform showing fund selection"
                    className="preview-screenshot"
                  />
                </div>
                <p className="preview-caption">Choose from Franklin Templeton managed funds and start investing with as little as $250</p>
              </div>

              <div className="preview-item">
                <div className="preview-label">
                  <span className="preview-title">Track your growth</span>
                </div>
                <div className="preview-image-container">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_simply-landing/artifacts/8hvelpc5_Track%20your%20growth.jpg"
                    alt="Portfolio dashboard showing account valuation and performance"
                    className="preview-screenshot"
                  />
                </div>
                <p className="preview-caption">Monitor your portfolio performance, track investments, and manage your money anytime, anywhere</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Audience Segments */}
      <section className="audience-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Investing goals made simple</h2>
            <p className="section-subtitle">Whatever your goal, we've got you covered</p>
          </div>

          <div className="audience-grid-modern">
            {audienceSegments.map((segment, index) => {
              const Icon = audienceIcons[segment.icon];
              
              return (
                <div key={index} className={`audience-card-modern audience-card-${index + 1}`}>
                  <div className="audience-card-overlay"></div>
                  <div className="audience-card-inner">
                    <div className="audience-icon-large">
                      <Icon className="h-12 w-12" />
                    </div>
                    <h3 className="audience-title-modern">{segment.title}</h3>
                    <p className="audience-description-modern">{segment.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="comparison-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why choose SimpleInvest?</h2>
            <p className="section-subtitle">Compare and see the difference</p>
          </div>

          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>DIY Trading</th>
                  <th className="highlight-column">SimpleInvest</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Professional management</td>
                  <td><span className="cross">✗</span></td>
                  <td className="highlight-column"><span className="check-primary">✓</span></td>
                </tr>
                <tr>
                  <td>Transparent fees</td>
                  <td><span className="check">✓</span></td>
                  <td className="highlight-column"><span className="check-primary">✓</span></td>
                </tr>
                <tr>
                  <td>App + portal access</td>
                  <td><span className="check">✓</span></td>
                  <td className="highlight-column"><span className="check-primary">✓</span></td>
                </tr>
                <tr>
                  <td>Easy setup (minutes)</td>
                  <td><span className="cross">✗</span></td>
                  <td className="highlight-column"><span className="check-primary">✓</span></td>
                </tr>
                <tr>
                  <td>Franklin Templeton backed</td>
                  <td><span className="cross">✗</span></td>
                  <td className="highlight-column"><span className="check-primary">✓</span></td>
                </tr>
                <tr>
                  <td>Educational resources</td>
                  <td><span className="cross">✗</span></td>
                  <td className="highlight-column"><span className="check-primary">✓</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="container">
          <div className="final-cta-grid">
            <div className="final-cta-content">
              <h2 className="final-cta-title">Ready to grow your wealth?</h2>
              <p className="final-cta-subtitle">Start your investment journey today – it only takes minutes</p>
              <Button 
                onClick={handleRegisterClick}
                size="lg"
                className="primary-cta large"
              >
                Start investing today
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
            </div>
            <div className="final-cta-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_simply-landing/artifacts/rnl6kzir_simpleinvest-signup.jpg"
                alt="SimpleInvest signup interface"
                className="cta-screenshot"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Legal Information */}
      <section className="legal-section">
        <div className="container">
          <h3 className="legal-title">Important legal information</h3>
          <div className="legal-text">
            <p>
              SimpleInvest is distributed by Partnervest Pty Ltd (ACN 683 034 693), a Corporate Authorised 
              Representative (1317675) of Industry Funds Services Ltd (ACN 007 016 195, AFSL 232514), and 
              administered by OpenInvest Limited (ACN 614 587 183) via the OpenInvest Portfolio Service 
              (ARSN 628 156 052).
            </p>
            <p>
              This website provides factual information about the service. Any general advice contained does 
              not consider your objectives, financial situation, or needs.
            </p>
            <p>
              Before making an investment decision, please review the relevant Product Disclosure Statement (PDS), 
              Target Market Determination (TMD), and associated reference documents, available in the Investor Portal.
            </p>
            <p>
              Neither Partnervest Pty Ltd nor Franklin Templeton guarantees the performance of any fund or the 
              repayment of your capital. Past performance is neither an indicator nor a guarantee of future results. 
              All investments carry risks, including potential loss of principal.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <p className="footer-text">© Partnervest 2026</p>
            <p className="footer-text">Developed by Corsa Media Pty Ltd</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
